import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NfcCard } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { ErrorLine } from '../../components/ErrorPanel';
import { Badge, Button, ConfirmButton } from '../../components/ui';
import { NFC } from './api';

export const CARDS_KEY = ['w18', 'nfc', 'cards'] as const;

const TONE: Record<NfcCard['status'], 'good' | 'bad' | 'warn' | 'neutral'> = {
  active: 'good',
  blocked: 'bad',
  lost: 'bad',
  retired: 'neutral',
};

export function CardStatus({ card }: { card: NfcCard }) {
  return (
    <Badge tone={TONE[card.status]}>
      {t(`w18.nfc.status.${card.status}`)}
      {card.blockReason && card.status !== 'active' ? ` (${t(`w18.nfc.reason.${card.blockReason}`)})` : ''}
    </Badge>
  );
}

/** What can be done to a card in its current state. A stolen card can never be unblocked (contract nfc-cards s3). */
export function cardActions(card: NfcCard): {
  block: boolean;
  unblock: boolean;
  retire: boolean;
} {
  return {
    block: card.status === 'active',
    unblock: (card.status === 'blocked' || card.status === 'lost') && card.blockReason !== 'stolen',
    retire: card.status !== 'retired',
  };
}

/** Block (with a reason), unblock and retire buttons for one card. */
export function CardActions({ card, canManage }: { card: NfcCard; canManage: boolean }) {
  const qc = useQueryClient();
  const [blocking, setBlocking] = useState(false);
  const [reason, setReason] = useState<'lost' | 'stolen' | 'other'>('lost');
  const done = () => qc.invalidateQueries({ queryKey: CARDS_KEY });
  const block = useMutation({
    mutationFn: () =>
      api.post(`${NFC}/${card.id}/block`, {
        body: { reason },
        schema: NfcCard,
      }),
    onSuccess: async () => {
      setBlocking(false);
      await done();
    },
  });
  const unblock = useMutation({
    mutationFn: () => api.post(`${NFC}/${card.id}/unblock`, { schema: NfcCard }),
    onSuccess: done,
  });
  const retire = useMutation({
    mutationFn: () => api.post(`${NFC}/${card.id}/retire`, { schema: NfcCard }),
    onSuccess: done,
  });
  if (!canManage) return null;
  const can = cardActions(card);
  const name = card.label || card.hint;
  if (blocking) {
    return (
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          block.mutate();
        }}
      >
        <div>
          <label htmlFor={`w18-reason-${card.id}`} className="mb-1 block text-xs font-medium text-slate-800">
            {t('w18.nfc.blockReason')}
          </label>
          <select
            id={`w18-reason-${card.id}`}
            className="min-h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
            value={reason}
            onChange={(e) => setReason(e.target.value as typeof reason)}
          >
            <option value="lost">{t('w18.nfc.reason.lost')}</option>
            <option value="stolen">{t('w18.nfc.reason.stolen')}</option>
            <option value="other">{t('w18.nfc.reason.other')}</option>
          </select>
        </div>
        <Button type="submit" variant="danger" disabled={block.isPending}>
          {t('w18.nfc.block')}
        </Button>
        <Button onClick={() => setBlocking(false)}>{t('common.cancel')}</Button>
        <ErrorLine error={block.error} />
      </form>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      {can.block ? (
        <Button onClick={() => setBlocking(true)} aria-label={t('w18.nfc.blockName', { name })}>
          {t('w18.nfc.block')}
        </Button>
      ) : null}
      {can.unblock ? (
        <Button onClick={() => unblock.mutate()} disabled={unblock.isPending} aria-label={t('w18.nfc.unblockName', { name })}>
          {t('w18.nfc.unblock')}
        </Button>
      ) : null}
      {card.blockReason === 'stolen' && card.status !== 'retired' ? <span className="text-xs text-slate-600">{t('w18.nfc.stolenNote')}</span> : null}
      {can.retire ? <ConfirmButton label={t('w18.nfc.retire')} onConfirm={() => retire.mutate()} pending={retire.isPending} /> : null}
      <ErrorLine error={unblock.error ?? retire.error} />
    </div>
  );
}

/** Last four characters of a card number, for recognising it (the number itself is never stored). */
export function hintText(hint: string): string {
  return hint ? `•••• ${hint.toUpperCase()}` : '';
}
